import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  Locale,
  VueAtlasService,
  VueLocaleService, VuePortalService,
  VueSessionCookieService, WINDOW
} from '@vue/vueframework';
import { combineLatest, forkJoin, interval, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { makeHeader } from './shared/connect.util';
import { TestCenterService } from './shared/services/test-center.service';

@Component({
  selector: 'app-component',
  styleUrls: [ './app.component.scss' ],
  templateUrl: './app.component.html',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * List of locales for the
   * locale selector.
   */
  public locales: Observable<Locale[]>;

  public selectedLocale: Locale;

  public showUnsupportedBrowser = false;

  public siteHeaderDetail: string;

  public headerSub: Subscription;

  public accountMaintenanceLink = '/Connect/#/account/maintenance';
  public constructor(
    private portalService: VuePortalService,
    private atlasService: VueAtlasService,
    private localeService: VueLocaleService,
    @Inject(WINDOW) private vueWindow: Window,
    private testCenterService: TestCenterService,
    private vueSessionCookieService: VueSessionCookieService,
  ) {
    this.showUnsupportedBrowser = window.showUnsupportedBrowser;
  }

  public ngOnInit(): void {
    const currentLocale = this.localeService.getLocaleFromCookie();
    this.locales = forkJoin([
      this.portalService.getLocaleSettings(null, '/rest/connect/localeSettings'),
      this.atlasService.getAllLocales()
    ]).pipe(
      map(([ localeSettings, allLocales ]) => {
        return localeSettings.supportedLocales.map((sl) => {
          const locale = allLocales.find((l) => l.localeTag.toLowerCase() === sl.toLowerCase());
          return {
            code: locale.localeTag,
            name: locale.localizedShortDescription
          };
        });
      }),
      tap((locales) => {
        this.selectedLocale = locales.find((l) => l.code.toLowerCase() === currentLocale.toLowerCase());
      })
    );
    this.setHeader();
  }

  public ngOnDestroy(): void {
    this.headerSub.unsubscribe();
  }

  public hideNotification(): void {
    const unsupportedBrowserSection = document.getElementById('unsupportedBrowserSection');

    /* istanbul ignore if */
    if (unsupportedBrowserSection !== null) {
      window.dismissUnsupportedBrowser();
    }
  }

  public localeChanged(ev: Locale): void {
    this.localeService.setLocale(ev.code, true);
    this.vueWindow.location.reload();
  }

  /**
   * Watching siteId to set site header. Site id might change
   * if person logged out and logs back in so we re-check
   */
  public setHeader(): void {
    this.headerSub = interval(500).pipe(
      switchMap(() => combineLatest([
        this.vueSessionCookieService.watchAttribute('realmDetail', 'VUE').pipe(map((v) => JSON.parse(v))),
        this.vueSessionCookieService.watchAttribute('siteID', 'VUE').pipe(map((v) => JSON.parse(v)))
      ])),
      map(([ realm, siteId ]) => siteId || realm?.siteID),
      distinctUntilChanged(),
      switchMap((selectedSiteId) =>
        !selectedSiteId ? of(null) : this.testCenterService.getTestCenterDetails(selectedSiteId)),
    ).subscribe((r) => {
      if (!r) {
        this.siteHeaderDetail = '';
        return;
      }
      const { name, id } = r[ 0 ];
      this.siteHeaderDetail = makeHeader(name, id);
    });
  }
}
